import { MutableRefObject, useContext, useEffect } from "react";
import { useTypedFastStorage } from "../../hooks/useFastStorage";
import ApplicationContext, { IApplicationContext } from "../../ApplicationContext";
import { AesUsageArgs } from "../../fms.report/args";
import SecondaryButton from "../../ui/SecondaryButton";
import CheckBox from "../../ui/CheckBox";
import IfmsDate from '../../ui/Date';
import InlineInputControlRow from "../../ui/layout/InlineInputControlRow";
import Label from "../../ui/Label";
import RadioButton from "../../ui/RadioButton";
import useGlobalOkDialog from "../../hooks/useGlobalOkDialog";
import moment from 'moment';
import { nowDate, addMonths, addDays } from "../../dateUtils";

interface UsageReportViewParams {
  onPopupClose?: () => void;
}

interface UsageReportLoadedDetailsViewParams {
  UsageReport: AesUsageArgs;
  UsageReportRef: MutableRefObject<AesUsageArgs | null>;
  setUsageReport: (x: Partial<AesUsageArgs>) => void;
  onPopupClose?: () => void;
  appContext: IApplicationContext;
}

function getStorageKey() {
  return 'print-aes-usage-report';
}

function UsageReportImpl(params: UsageReportViewParams) {
  const appContext = useContext(ApplicationContext);
  const user = appContext.user;
  const [inStorage, setInStorage, removeFromStorage, UsageReportRef] = useTypedFastStorage<AesUsageArgs>(getStorageKey(), null);

  useEffect(() => {
    initialize();
    return () => { removeFromStorage(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initialize() {
    const lastMonth = new Date(addMonths(nowDate(), -1));
    const firstDay = moment(new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1)).format();
    const lastDay = new Date(addMonths(addDays(firstDay, -1), 1));

    const data = new AesUsageArgs({
      startDate: moment(new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1)).format('YYYY-MM-DDT00:00:00'),
      endDate: moment(new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate())).format('YYYY-MM-DDT00:00:00'),
      user: 'U',
      showDetail: false,
      groupID: user?.suntekId ?? '',
    });
    setInStorage({ ...data });
  }

  if (!inStorage) return null;

  return (
    <UsageReportInitializedImpl
      UsageReport={inStorage}
      UsageReportRef={UsageReportRef}
      setUsageReport={setInStorage}
      onPopupClose={params.onPopupClose}
      appContext={appContext}
    />
  );
}

function UsageReportInitializedImpl({
  UsageReport,
  setUsageReport,
  onPopupClose,
  appContext,
}: UsageReportLoadedDetailsViewParams) {
  const [showGlobalOkDialog] = useGlobalOkDialog();

  function onChange(change: Partial<AesUsageArgs>) {
    setUsageReport({ ...change });
  }

  async function print() {
    try {
      const apiResult = await appContext.api.printAesUsage(UsageReport);
      if (apiResult?.success) {
        const newWindow = window.open();
        if (newWindow) {
          const iframe = document.createElement('iframe');
          iframe.src = 'data:application/pdf;base64,' + apiResult.result;
          iframe.style.cssText = 'border:0;width:100%;height:100%';
          newWindow.document.title = 'AES Usage Report';
          newWindow.document.body.style.margin = '0';
          newWindow.document.body.appendChild(iframe);
          newWindow.document.close();
        }
      } else {
        showGlobalOkDialog(<p>{apiResult?.message ?? 'Print failed'}</p>, false, null, 'warning');
      }
    } catch (error) {
      // silent
    }
  }

  return (
    <div className='report-popup'>
      <div className='report-row' style={{ overflow: 'hidden' }}>

        <InlineInputControlRow label='Period' mode='small' bold></InlineInputControlRow>
        <hr style={{ border: '0px', height: '1px', backgroundColor: '#0000FF', marginTop: '0px' }} />
        <div style={{ display: 'flex', paddingTop: '4px', marginBottom: '10px' }}>
          <div style={{ marginLeft: '65px', width: '60px' }}>
            <Label label='Date' bold style={{ width: '60px' }} />
          </div>
          <div style={{ width: '80px' }}>
            <IfmsDate dateId='aes-usage-fromdate' value={UsageReport?.startDate} onChange={startDate => onChange({ startDate })} />
          </div>
          <div style={{ width: '30px', paddingLeft: '20px' }}>~</div>
          <div style={{ width: '80px' }}>
            <IfmsDate dateId='aes-usage-todate' value={UsageReport?.endDate} onChange={endDate => onChange({ endDate })} />
          </div>
        </div>

        <div>
          <InlineInputControlRow label='Print Type' mode='small' bold></InlineInputControlRow>
        </div>
        <hr style={{ border: '0px', height: '1px', backgroundColor: '#FF0000', marginTop: '0px' }} />
        <div style={{ display: 'flex' }}>
          <div style={{ marginLeft: '100px', width: '150px', fontWeight: 'bold' }}>
            <RadioButton group='aes-usage-type' reverse label='User' checked={UsageReport?.user === 'U'} onChecked={() => onChange({ user: 'U' })} />
          </div>
          <div style={{ width: '140px', fontWeight: 'bold' }}>
            <RadioButton group='aes-usage-type' reverse label='USPPI' checked={UsageReport?.user === 'I'} onChecked={() => onChange({ user: 'I' })} />
          </div>
        </div>

        <div style={{ display: 'flex' }}>
          <div style={{ width: '160px', marginTop: '20px', marginBottom: '10px' }}>
            <CheckBox bold label='Print Detail' value={UsageReport?.showDetail ?? false} onChange={check => onChange({ showDetail: check })} />
          </div>
        </div>

      </div>
      <div className='clearfix' />
      <div className='report-footer'>
        <SecondaryButton name='aes-usage-ok-button' size='regular' onClick={print}>OK</SecondaryButton>
        <SecondaryButton name='aes-usage-cancel-button' size='regular' onClick={onPopupClose}>CANCEL</SecondaryButton>
      </div>
    </div>
  );
}

const AesUsageReportView = ({ onPopupClose }: { onPopupClose?: () => void }) => {
  return <UsageReportImpl onPopupClose={onPopupClose} key='aesUsageView' />;
};

export default AesUsageReportView;
